import React, { createContext, useContext, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { convertPixelsToGrid } from "../Pages/FlexibleLayout/utils";
import axiosInstance, { handleApiError } from "../services/axiosService";
import { processLayout } from "../utils/GridLayoutFunc";
import { organizeScreenFolders } from "../utils/screenutils";
import { useTreeContext } from "./TreeDataContext";
import { updateScreenChecksum } from "../utils/configChecksum/setterFunctions/screenConfig";
import { getTreeDataForScreens } from "../utils/treeData/treeDataUtils";
import { normalizeMenuItems } from "../utils/InitialRenderingData";

const ScreenContextApi = createContext();

const useScreenContext = () => useContext(ScreenContextApi);

const ScreenContext = ({ children }) => {
  const [selectedScreen, setSelectedScreen] = useState({
    id: "",
    name: "",
    parentData: "",
    type: ""
  });

  const [layouts, setLayouts] = useState([]);
  const layoutData = useRef({});
  const nestedGridData = useRef({});

  const [currentScreenData, setCurrentScreenData] = useState({
    description: "",
    info_tag: [],
    folder_info_tag: [],
    header_config: {
      menu: [],
      logo: {},
      style: {},
      menu_bottom: []
    },
    module_header_config: null,
    z_admin_config: { menu: [] },
    show_modules_in_sidebar: false
  });
  // const { currentChecksum } = useAppOrgContext();
  const { setScreensExplorerData } = useTreeContext();
  const { screenId } = useParams();
  const [checkedBlocks, setCheckedBlocks] = useState({
    table: [],
    tag: []
  });
  const [layoutConfig, setLayoutConfig] = useState([]);
  const headerData = useRef({});

  const fetchAllScreens = async ({ signal } = {}) => {
    const [screensRes, folderTagsRes] = await Promise.all([
      axiosInstance.get("/screens", { signal }),
      axiosInstance.get("/folder-tag", { signal })
    ]);

    const screensData = screensRes.data;
    const folderTagsData = folderTagsRes.data;

    const treeData = getTreeDataForScreens(screensData, folderTagsData);

    setScreensExplorerData(treeData);
    settingHeaderData(treeData);

    // Return a clear, structured object
    return {
      data: screensData,
      folderTagsData,
      treeData
    };
  };

  const settingHeaderData = (screensExplorerData) => {
    const headers = {};

    screensExplorerData.forEach((screen) => {
      const headerBlock = screen.items?.find((item) => item.name === "header");
      if (headerBlock) {
        headers[screen.id] = headerBlock.id;
      }
    });

    headerData.current = headers;
  };

  const cleanConfigItems = (items, keysToExclude) => {
    return items.map((item) => {
      const cleanedItem = Object.fromEntries(Object.entries(item).filter(([key]) => !keysToExclude.includes(key)));

      if (cleanedItem.dropdown && cleanedItem.dropdown.length > 0) {
        cleanedItem.dropdown = cleanConfigItems(cleanedItem.dropdown, keysToExclude);
      }

      return cleanedItem;
    });
  };

  const cleanHeaderConfig = (config) => {
    const { menu, menu_bottom, ...restHeaderConfig } = config.header_config;
    const keysToExclude = ["selected", "show", "chosen"];
    const normalizedMenu = normalizeMenuItems(menu || []);
    const normalizedMenuBottom = normalizeMenuItems(menu_bottom || []);
    return {
      ...config,
      header_config: {
        ...restHeaderConfig,
        menu: cleanConfigItems(normalizedMenu, keysToExclude),
        menu_bottom: cleanConfigItems(normalizedMenuBottom, keysToExclude)
      }
    };
  };

  const cleanModuleHeaderConfig = (moduleHeaderConfigArray) => {
    const keysToExclude = ["selected", "show", "chosen"];
    return moduleHeaderConfigArray.map((moduleHeaderConfig) => {
      const { config, ...restModuleHeaderConfig } = moduleHeaderConfig;
      const normalizedMenu = normalizeMenuItems(config.menu || []);
      const normalizedMenuBottom = normalizeMenuItems(config.menu_bottom || []);
      return {
        ...restModuleHeaderConfig,
        config: {
          ...config,
          menu: cleanConfigItems(normalizedMenu, keysToExclude),
          menu_bottom: cleanConfigItems(normalizedMenuBottom, keysToExclude),
          logo: config.logo || {},
          style: config.style || {}
        }
      };
    });
  };

  const getScreenConfig = () => {
    const type = selectedScreen.type;

    if (type === "header") {
      let data = {
        header_config: currentScreenData.header_config,
        z_admin_config: currentScreenData.z_admin_config,
        module_header_config: currentScreenData.module_header_config,
        show_modules_in_sidebar: currentScreenData.show_modules_in_sidebar || false
      };

      // Clean the configs
      data = cleanHeaderConfig(data);
      data.module_header_config = cleanModuleHeaderConfig(data.module_header_config);

      return data; // This will be the `body` for headers
    }

    if (type === "page" || type === "module_header") {
      const newLayoutsArray = [...layouts];
      const { elements } = processLayout(layoutData, nestedGridData, newLayoutsArray);

      let newLayoutConfig = layoutConfig;
      if (currentScreenData.device === "mobile") {
        const gridSize = currentScreenData.position_screen_metadata?.grid_size || 4;
        newLayoutConfig = convertPixelsToGrid(newLayoutConfig, gridSize);
      }

      return {
        config: { layout: elements },
        position_screen_config: newLayoutConfig,
        description: currentScreenData.description,
        info_tag: currentScreenData.info_tag,
        folder_info_tag: currentScreenData.folder_info_tag,
        is_positioned_screen: currentScreenData.is_positioned_screen
      };
    }
  };

  const saveScreenJson = async () => {
    try {
      let body = getScreenConfig();
      // if (selectedScreen.type === "header") {
      //   data = cleanHeaderConfig(data);
      // }

      // if (selectedScreen.type === "header") {
      //   data.module_header_config = cleanModuleHeaderConfig(data.module_header_config);
      // }

      // let body = {};
      // if (selectedScreen.type === "header") {
      //   body = data;
      // } else if (selectedScreen.type === "page" || selectedScreen.type === "module_header") {
      //   let newLayoutConfig = layoutConfig;
      //   if (currentScreenData.device === "mobile") {
      //     newLayoutConfig = convertPixelsToGrid(newLayoutConfig, currentScreenData.position_screen_metadata?.grid_size);
      //   }
      //   body = {
      //     config: data,
      //     position_screen_config: newLayoutConfig,
      //     description: currentScreenData.description,
      //     info_tag: currentScreenData.info_tag,
      //     folder_info_tag: currentScreenData.folder_info_tag
      //   };
      // }

      const { message } = await axiosInstance.patch(`/screens/${screenId}`, body);
      const { data: folderTagsData } = await axiosInstance.get("/folder-tag");

      const deviceIndex = currentScreenData.device === "desktop" ? 0 : 1;
      let updatedScreenItem;

      setScreensExplorerData((prevData) => {
        const deviceFolder = prevData[deviceIndex];

        let screenItem;
        deviceFolder.items.forEach((item) => {
          if (item.isFolder && item.items) {
            const found = item.items.find((subItem) => subItem.id === screenId);
            if (found) screenItem = found;
          } else if (item.id === screenId) {
            screenItem = item;
          }
        });

        if (screenItem) {
          updatedScreenItem = {
            ...screenItem,
            fileInfo: {
              ...screenItem.fileInfo,
              folder_info_tag: body.folder_info_tag
            }
          };
        }

        return organizeScreenFolders(
          prevData,
          updatedScreenItem,
          currentScreenData.device,
          body.folder_info_tag,
          folderTagsData,
          screenId
        );
      });
      updateScreenChecksum(body, selectedScreen.type);
      // const newChecksum = createChecksum(data);
      // currentChecksum.current = newChecksum;
      return { data: body, message };
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  };

  const payloadStateList = [currentScreenData, layouts, layoutConfig, layoutData.current, nestedGridData.current];

  return (
    <ScreenContextApi.Provider
      value={{
        selectedScreen,
        setSelectedScreen,
        headerData,
        checkedBlocks,
        setCheckedBlocks,
        layoutData,
        layouts,
        nestedGridData,
        setLayouts,
        fetchAllScreens,
        currentScreenData,
        setCurrentScreenData,
        saveScreenJson,
        getScreenConfig,
        layoutConfig,
        setLayoutConfig,
        payloadStateList
      }}
    >
      {children}
    </ScreenContextApi.Provider>
  );
};

export default ScreenContext;
export { useScreenContext };
